import {
  CustomFieldSchema,
  CustomFieldType,
  CustomFieldValidators,
  DateTypeOptions,
} from '@tribeplatform/gql-client/types'
import { CustomFieldSubtype } from '@tribeplatform/react-components/CustomField'
import { CustomFieldValidatorPattern } from '@tribeplatform/react-components/CustomFields'

/**
 * ? I18n Issue
 * The `name` seems to be persisted in backend (it's a content) and should not be translated
 * as static texts. Need to find another approach.
 */
export const suggestedFields: CustomFieldSchema[] = [
  {
    name: 'Website',
    key: 'website',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.URL) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.pattern,
        value: JSON.stringify(CustomFieldValidatorPattern.URL),
        customErrorMessage: 'Website link should start with https://',
      },
    ],
  },
  {
    name: 'Birthday',
    key: 'birthday',
    type: CustomFieldType.date,
    typeOptions: {
      dateType: DateTypeOptions.datetime,
    },
    settings: [
      {
        key: 'subtype',
        value: '"date"',
      },
      {
        key: 'view',
        value: '"Date"',
      },
    ],
  },
  {
    name: 'Job title',
    key: 'jobTitle',
    type: CustomFieldType.text,
  },
  {
    name: 'Pronoun',
    key: 'pronoun',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.SELECT) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.enum,
        value: JSON.stringify(['She/Her', 'He/Him', 'They/Them']),
      },
    ],
  },
  {
    name: 'Bio',
    key: 'bio',
    type: CustomFieldType.richText,
  },
  {
    name: 'Featured',
    key: 'featured',
    type: CustomFieldType.boolean,
  },
  {
    name: 'Company',
    key: 'company',
    type: CustomFieldType.text,
  },
  {
    name: 'Location',
    key: 'location',
    type: CustomFieldType.text,
  },
  {
    name: 'Country',
    key: 'country',
    type: CustomFieldType.text,
    settings: [
      { key: 'inputComponent', value: JSON.stringify('CountryPicker') },
    ],
  },
  {
    name: 'City',
    key: 'city',
    type: CustomFieldType.text,
  },
  {
    name: 'Gender',
    key: 'gender',
    type: CustomFieldType.text,
    settings: [
      { key: 'subtype', value: JSON.stringify(CustomFieldSubtype.SELECT) },
    ],
    validators: [
      {
        validation: CustomFieldValidators.enum,
        value: JSON.stringify(['Male', 'Female', 'Other']),
      },
    ],
  },
  ...[
    {
      name: 'X',
      key: 'twitter',
      regex: CustomFieldValidatorPattern.TWITTER_PROFILE,
      placeholder: 'https://x.com/username',
    },
    {
      name: 'Instagram',
      key: 'instagram',
      regex: CustomFieldValidatorPattern.INSTAGRAM_PROFILE,
      placeholder: 'https://instagram.com/username',
    },
    {
      name: 'Facebook',
      key: 'facebook',
      regex: CustomFieldValidatorPattern.FACEBOOK_PROFILE,
      placeholder: 'https://facebook.com/username',
    },
    {
      name: 'Youtube channel',
      key: 'youtubeChannel',
      icon: 'youtube',
      regex: CustomFieldValidatorPattern.YOUTUBE_CHANNEL,
      placeholder: 'https://youtube.com/channel/abcdefg',
    },
    {
      name: 'LinkedIn',
      key: 'linkedin',
      regex: CustomFieldValidatorPattern.LINKEDIN_PROFILE,
      placeholder: 'https://linkedin.com/in/username',
    },
    {
      name: 'Github',
      key: 'github',
      regex: CustomFieldValidatorPattern.GITHUB_PROFILE,
      placeholder: 'https://github.com/username',
    },
    {
      name: 'Telegram',
      key: 'telegram',
      regex: CustomFieldValidatorPattern.TELEGRAM_PROFILE,
      placeholder: 'https://t.me/username',
    },
    {
      name: 'Snapchat',
      key: 'snapchat',
      regex: CustomFieldValidatorPattern.SNAPCHAT_PROFILE,
      placeholder: 'https://snapchat.com/add/username',
    },
    {
      name: 'Medium',
      key: 'medium',
      regex: CustomFieldValidatorPattern.MEDIUM_PROFILE,
      placeholder: 'https://medium.com/@username',
    },
    {
      name: 'Reddit',
      key: 'reddit',
      regex: CustomFieldValidatorPattern.REDDIT_PROFILE,
      placeholder: 'https://reddit.com/user/username',
    },
    {
      name: 'Stack Overflow',
      key: 'stackoverflow',
      regex: CustomFieldValidatorPattern.STACKOVERFLOW_PROFILE,
      placeholder: 'https://stackoverflow.com/users/123/username',
    },
    {
      name: 'Mastodon',
      key: 'mastodon',
      regex: CustomFieldValidatorPattern.MASTODON_ADDRESS,
      placeholder: '@john@smith.com or john@smith.com',
    },
  ].map(({ icon, name, key, regex, placeholder }) => ({
    name,
    key,
    type: CustomFieldType.text,
    settings: [
      key !== 'mastodon' && {
        key: 'subtype',
        value: JSON.stringify(CustomFieldSubtype.URL),
      },
      {
        key: 'placeholder',
        value: JSON.stringify(placeholder),
      },
      { key: 'view', value: JSON.stringify('SocialMedia') },
      { key: 'icon', value: JSON.stringify(icon || key) },
      { key: 'section', value: JSON.stringify('Social links') },
      { key: 'category', value: JSON.stringify('socialLink') },
      { key: 'locked', value: JSON.stringify(true) },
    ].filter(setting => !!setting),
    validators: [
      {
        validation: CustomFieldValidators.pattern,
        value: JSON.stringify(regex),
        customErrorMessage: `Please enter a valid ${name} link`,
      },
    ],
  })),
]
